/**
 * `/lineup/:matchId` — the lineup approval + edit surface for the captain
 * SPA (web UI Phase 2 — lineup editor chunk 1/2).
 *
 * This is the page the iMessage bot links Amy to when she wants to
 * approve or edit a lineup (`src/bot/dispatcher.ts` →
 * `https://app.nofuzz.net/lineup/${match_id}`). The route param is the
 * MATCH id, not the lineup id: the bot resolves a match for the captain;
 * `propose_lineup` + `get_lineup_card` both key on `match_id`;
 * `approve_lineup` keys on a `lineup_id` which this page obtains FROM the
 * chosen proposal (returned by `propose_lineup`) or the current card.
 * Keying the URL on match_id is the only param that makes the page work
 * from a bot-supplied link.
 *
 * AGENTS.md rule 2 — lineups are NEVER auto-approved. This page is the
 * explicit human confirmation surface. There is no code path that calls
 * `approve_lineup` without the captain tapping the Approve button.
 *
 * Edit UI: per-line dropdowns (player1, player2). Drag-to-swap was the
 * nice-to-have; dropdowns are the minimal correct thing for a 4-line v0.1
 * editor and ship zero extra bundle (no DnD lib). The dropdown options
 * are the union of all players across the two proposals — that's exactly
 * the available top-8 pool, which is the correct constraint set for a
 * valid WTTA lineup anyway (only available, top-strength players belong
 * on the card). No separate roster-read endpoint exists or is needed.
 *
 * Data access is exclusively via the HTTP tool gateway through
 * `lib/lineup-api.ts`. The browser has only the session cookie; it never
 * touches Supabase or the MCP server directly.
 */

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useParams } from "react-router-dom";
import {
  approveLineup,
  getLineupCard,
  proposeLineup,
  LineupToolError,
  type ApprovedLineup,
  type LineupCard,
  type LineupEdit,
  type LineupProposals,
  type PlayerRef,
  type ProposedLineup,
} from "../lib/lineup-api";
import { useAuthedSession } from "../components/RequireAuth";

type LineNum = 1 | 2 | 3 | 4;
type Kind = "straight" | "stacked";

/** One editable line: the current player1/player2 ids the captain picked. */
type EditableLine = {
  line_number: LineNum;
  player1_id: string;
  player2_id: string;
};

type LoadState =
  | { kind: "loading" }
  | {
      kind: "ready";
      /** Current approved lineup card, if one exists. */
      card: LineupCard | null;
      /** Draft proposals, if they exist. Null until generated/loaded. */
      proposals: LineupProposals | null;
    }
  | { kind: "error"; message: string };

// ─── Error-code → captain copy ─────────────────────────────────────────
//
// The gateway scrubs the real MCP error message and only preserves the
// `code`. So we re-attach human copy here. Unknown codes fall back to a
// generic line that still names the code so a captain can screenshot it.

function explainError(e: LineupToolError): string {
  switch (e.code) {
    case "WTTA_DESCENDING_VIOLATED":
      return "That swap breaks the WTTA rule — each line's combined NTRP must be strictly lower than the line above it. Adjust a player and try again.";
    case "NO_APPROVED_LINEUP":
      return "No approved lineup for this match yet.";
    case "BAD_INPUT":
    case "INVALID_EDIT":
      return "That edit isn't valid (a player may be doubled-up across lines, or not on the roster). Fix the highlighted line and retry.";
    case "MATCH_NOT_FOUND":
      return "That match wasn't found. The link may be stale — re-check with the bot.";
    case "NOT_ENOUGH_PLAYERS":
      return "Not enough available players (need 8). Collect more RSVPs, then re-generate.";
    case "RATE_LIMITED":
      return "Too many requests. Wait a minute, then retry.";
    case "UNAUTHENTICATED":
      return "Your session expired. Reload the page to sign in again.";
    case "NOT_FOUND":
      return "You don't have permission to do that on this team.";
    default:
      return `The server reported an error (${e.code}). Try again, or re-check with the bot.`;
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────

/** Union of every player across both proposals → dropdown option pool. */
function rosterFromProposals(proposals: ProposedLineup[]): PlayerRef[] {
  const byId = new Map<string, PlayerRef>();
  for (const p of proposals) {
    for (const l of p.lines) {
      byId.set(l.player1.id, l.player1);
      byId.set(l.player2.id, l.player2);
    }
  }
  return [...byId.values()].sort((a, b) => b.ntrp - a.ntrp);
}

/** Editable lines seeded from a proposal's lines, sorted by line_number. */
function seedEditable(p: ProposedLineup): EditableLine[] {
  return p.lines
    .slice()
    .sort((a, b) => a.line_number - b.line_number)
    .map((l) => ({
      line_number: l.line_number,
      player1_id: l.player1.id,
      player2_id: l.player2.id,
    }));
}

/** Diff editable lines against the base proposal → approve_lineup edits. */
function diffEdits(
  base: ProposedLineup,
  edited: EditableLine[],
): LineupEdit[] {
  const baseByLine = new Map(base.lines.map((l) => [l.line_number, l]));
  const edits: LineupEdit[] = [];
  for (const e of edited) {
    const b = baseByLine.get(e.line_number);
    if (!b) continue;
    const edit: LineupEdit = { line_number: e.line_number };
    let changed = false;
    if (e.player1_id !== b.player1.id) {
      edit.player1_id = e.player1_id;
      changed = true;
    }
    if (e.player2_id !== b.player2.id) {
      edit.player2_id = e.player2_id;
      changed = true;
    }
    if (changed) edits.push(edit);
  }
  return edits;
}

function nameOf(roster: PlayerRef[], id: string): string {
  return roster.find((p) => p.id === id)?.name ?? "—";
}

// ─── Page ──────────────────────────────────────────────────────────────

export default function LineupEditor() {
  // Behind RequireAuth; calling this asserts a live session.
  useAuthedSession();
  const { matchId } = useParams<{ matchId: string }>();

  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [chosenKind, setChosenKind] = useState<Kind>("straight");
  const [editable, setEditable] = useState<EditableLine[]>([]);
  const [busy, setBusy] = useState<null | "generate" | "approve">(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [approved, setApproved] = useState<ApprovedLineup | null>(null);

  // ─── Initial load: try the current card AND draft proposals. ─────────
  useEffect(() => {
    if (!matchId) return;
    let cancelled = false;
    setState({ kind: "loading" });
    (async () => {
      // Card read first (cheap; tells us if an approved lineup exists).
      let card: LineupCard | null = null;
      try {
        card = await getLineupCard(matchId);
      } catch (e) {
        if (e instanceof LineupToolError && e.code === "NO_APPROVED_LINEUP") {
          card = null; // expected — no approved lineup yet.
        } else if (e instanceof LineupToolError && e.httpStatus === 401) {
          if (!cancelled)
            setState({
              kind: "error",
              message: "Your session expired. Reload to sign in again.",
            });
          return;
        }
        // Any other card error is non-fatal here — we still try proposals.
      }

      // Draft proposals: generate-on-load is intentional. propose_lineup
      // is idempotent (re-deletes stale drafts) so loading the page can't
      // corrupt an approved lineup, and the captain almost always wants
      // to see options when she follows the bot link. If propose fails
      // (e.g. NOT_ENOUGH_PLAYERS) we still render the card-only view.
      let proposals: LineupProposals | null = null;
      try {
        proposals = await proposeLineup(matchId);
      } catch {
        proposals = null;
      }

      if (cancelled) return;
      if (!card && !proposals) {
        setState({
          kind: "error",
          message:
            "Couldn't load a lineup or generate proposals for this match. The link may be stale, or the roster may not have 8 available players yet.",
        });
        return;
      }
      if (proposals && proposals.proposals.length > 0) {
        const straight =
          proposals.proposals.find((p) => p.proposal_kind === "straight") ??
          proposals.proposals[0];
        setChosenKind(
          (straight.proposal_kind as Kind) ?? "straight",
        );
        setEditable(seedEditable(straight));
      }
      setState({ kind: "ready", card, proposals });
    })();
    return () => {
      cancelled = true;
    };
  }, [matchId]);

  const proposals =
    state.kind === "ready" ? state.proposals : null;
  const card = state.kind === "ready" ? state.card : null;

  const roster = useMemo(
    () => (proposals ? rosterFromProposals(proposals.proposals) : []),
    [proposals],
  );

  const chosenProposal: ProposedLineup | null = useMemo(() => {
    if (!proposals) return null;
    return (
      proposals.proposals.find((p) => p.proposal_kind === chosenKind) ??
      proposals.proposals[0] ??
      null
    );
  }, [proposals, chosenKind]);

  function switchKind(kind: Kind) {
    if (!proposals) return;
    const next =
      proposals.proposals.find((p) => p.proposal_kind === kind) ??
      proposals.proposals[0];
    if (!next) return;
    setChosenKind(kind);
    setEditable(seedEditable(next)); // discard in-progress swaps on toggle
    setActionError(null);
  }

  function setLinePlayer(
    line_number: LineNum,
    slot: "player1_id" | "player2_id",
    playerId: string,
  ) {
    setEditable((prev) =>
      prev.map((l) =>
        l.line_number === line_number ? { ...l, [slot]: playerId } : l,
      ),
    );
    setActionError(null);
  }

  async function onGenerate() {
    if (!matchId) return;
    setBusy("generate");
    setActionError(null);
    try {
      const fresh = await proposeLineup(matchId);
      const straight =
        fresh.proposals.find((p) => p.proposal_kind === "straight") ??
        fresh.proposals[0];
      if (straight) {
        setChosenKind((straight.proposal_kind as Kind) ?? "straight");
        setEditable(seedEditable(straight));
      }
      setState((s) =>
        s.kind === "ready"
          ? { ...s, proposals: fresh }
          : { kind: "ready", card: null, proposals: fresh },
      );
    } catch (e) {
      setActionError(
        e instanceof LineupToolError
          ? explainError(e)
          : "Couldn't generate proposals. Retry.",
      );
    } finally {
      setBusy(null);
    }
  }

  async function onApprove() {
    if (!chosenProposal) return;
    setBusy("approve");
    setActionError(null);
    const edits = diffEdits(chosenProposal, editable);
    try {
      const result = await approveLineup(chosenProposal.lineup_id, edits);
      setApproved(result);
    } catch (e) {
      setActionError(
        e instanceof LineupToolError
          ? explainError(e)
          : "Approve failed. Retry.",
      );
    } finally {
      setBusy(null);
    }
  }

  // ─── Render ──────────────────────────────────────────────────────────

  if (state.kind === "loading") {
    return (
      <section>
        <p className="muted">Loading lineup…</p>
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section>
        <h1>Lineup</h1>
        <p role="alert" style={{ color: "#b3261e" }}>
          {state.message}
        </p>
      </section>
    );
  }

  // Approved confirmation state — terminal, replaces the editor.
  if (approved) {
    return (
      <section>
        <h1>Lineup approved ✅</h1>
        <p>
          Version <strong>{approved.version}</strong> ·{" "}
          <code>{approved.proposal_kind}</code>
          {approved.idempotent && (
            <span className="muted">
              {" "}
              (already approved with this exact lineup — no change)
            </span>
          )}
        </p>
        <table style={tableStyle}>
          <thead>
            <tr style={theadRow}>
              <th style={thLead}>Line</th>
              <th style={th}>Player 1</th>
              <th style={th}>Player 2</th>
              <th style={th}>Combined NTRP</th>
            </tr>
          </thead>
          <tbody>
            {approved.lines
              .slice()
              .sort((a, b) => a.line_number - b.line_number)
              .map((l) => (
                <tr key={l.line_number} style={tbodyRow}>
                  <td style={tdLead}>{l.line_number}</td>
                  <td style={td}>{l.player1.name}</td>
                  <td style={td}>{l.player2.name}</td>
                  <td style={td}>{l.combined_ntrp.toFixed(1)}</td>
                </tr>
              ))}
          </tbody>
        </table>
        <p className="muted" style={{ marginTop: "1rem" }}>
          <small>
            Screenshot or copy this onto the paper WTTA lineup form before
            the match. Text the bot if you need to change it.
          </small>
        </p>
      </section>
    );
  }

  const hasProposals =
    proposals !== null && proposals.proposals.length > 0;

  return (
    <section>
      <h1>Lineup</h1>

      {/* Current approved lineup (if any) — context before editing. */}
      {card && (
        <div
          style={{
            border: "1px solid #e5e5e5",
            borderRadius: 6,
            padding: "0.75rem 1rem",
            marginBottom: "1.5rem",
            background: "#fff",
          }}
        >
          <p style={{ margin: 0 }}>
            <strong>Current approved lineup</strong> — v{card.version} ·{" "}
            <code>{card.proposal_kind}</code>
          </p>
          <ol style={{ margin: "0.5rem 0 0", paddingLeft: "1.25rem" }}>
            {card.lines
              .slice()
              .sort((a, b) => a.line_number - b.line_number)
              .map((l) => (
                <li key={l.line_number}>
                  {l.player1.name} / {l.player2.name}
                  {l.combined_ntrp !== null && (
                    <span className="muted">
                      {" "}
                      ({l.combined_ntrp.toFixed(1)})
                    </span>
                  )}
                </li>
              ))}
          </ol>
          {hasProposals && (
            <p className="muted" style={{ margin: "0.5rem 0 0" }}>
              <small>
                Re-propose / edit below and approve to replace it (the
                prior version is kept).
              </small>
            </p>
          )}
        </div>
      )}

      {!hasProposals && (
        <div>
          <p className="muted">
            No draft proposals for this match yet.
          </p>
          <p>
            <button
              type="button"
              className="button"
              onClick={onGenerate}
              disabled={busy !== null}
            >
              {busy === "generate"
                ? "Generating…"
                : "Generate proposals"}
            </button>
          </p>
          {actionError && (
            <p role="alert" style={{ color: "#b3261e" }}>
              {actionError}
            </p>
          )}
        </div>
      )}

      {hasProposals && proposals && (
        <>
          {/* Side-by-side proposals (read-only reference). Single column
              on narrow screens (Amy is on her phone), two columns wide. */}
          <h2>Proposals</h2>
          {proposals.scouting_available ? (
            <p className="muted">
              <small>Opponent scouting context applied.</small>
            </p>
          ) : (
            <p className="muted">
              <small>
                No scouting brief yet — proposals use roster strength only.
              </small>
            </p>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "1rem",
              marginTop: "0.5rem",
            }}
          >
            {proposals.proposals.map((p) => {
              const isChosen = p.proposal_kind === chosenKind;
              return (
                <div
                  key={p.lineup_id}
                  style={{
                    border: isChosen
                      ? "2px solid #1a1a1a"
                      : "1px solid #e5e5e5",
                    borderRadius: 6,
                    padding: "0.75rem 1rem",
                    background: "#fff",
                  }}
                >
                  <h3 style={{ marginTop: 0, textTransform: "capitalize" }}>
                    {p.proposal_kind}
                    {isChosen && (
                      <span
                        className="muted"
                        style={{ fontWeight: 400 }}
                      >
                        {" "}
                        — selected
                      </span>
                    )}
                  </h3>
                  <ol
                    style={{
                      margin: 0,
                      paddingLeft: "1.1rem",
                      fontSize: "0.95rem",
                    }}
                  >
                    {p.lines
                      .slice()
                      .sort((a, b) => a.line_number - b.line_number)
                      .map((l) => (
                        <li
                          key={l.line_number}
                          style={{ marginBottom: "0.4rem" }}
                        >
                          <strong>
                            {l.player1.name} / {l.player2.name}
                          </strong>{" "}
                          <span className="muted">
                            ({l.combined_ntrp.toFixed(1)})
                          </span>
                          <br />
                          <span
                            className="muted"
                            style={{ fontSize: "0.85rem" }}
                          >
                            {l.rationale}
                          </span>
                        </li>
                      ))}
                  </ol>
                  {!isChosen && (
                    <p style={{ margin: "0.5rem 0 0" }}>
                      <button
                        type="button"
                        className="button button-ghost"
                        onClick={() =>
                          switchKind(p.proposal_kind as Kind)
                        }
                        disabled={busy !== null}
                      >
                        Use {p.proposal_kind}
                      </button>
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Editor — per-line dropdown swap on the chosen proposal. */}
          {chosenProposal && (
            <>
              <h2 style={{ marginTop: "1.5rem" }}>
                Edit &amp; approve{" "}
                <span
                  className="muted"
                  style={{
                    fontWeight: 400,
                    textTransform: "capitalize",
                  }}
                >
                  ({chosenKind})
                </span>
              </h2>
              <p className="muted">
                <small>
                  Swap any player below, then Approve. WTTA requires each
                  line's combined NTRP to be strictly lower than the line
                  above it — the server enforces this and will tell you if
                  a swap breaks it.
                </small>
              </p>
              <div style={{ overflowX: "auto" }}>
                <table className="stacked-fields" style={tableStyle}>
                  <thead>
                    <tr style={theadRow}>
                      <th style={thLead}>Line</th>
                      <th style={th}>Player 1</th>
                      <th style={th}>Player 2</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editable.map((l) => (
                      <tr key={l.line_number} style={tbodyRow}>
                        <td data-label="Line" style={tdLead}>
                          {l.line_number}
                        </td>
                        <td data-label="Player 1" style={td}>
                          <PlayerSelect
                            roster={roster}
                            value={l.player1_id}
                            disabled={busy !== null}
                            onChange={(id) =>
                              setLinePlayer(
                                l.line_number,
                                "player1_id",
                                id,
                              )
                            }
                          />
                        </td>
                        <td data-label="Player 2" style={td}>
                          <PlayerSelect
                            roster={roster}
                            value={l.player2_id}
                            disabled={busy !== null}
                            onChange={(id) =>
                              setLinePlayer(
                                l.line_number,
                                "player2_id",
                                id,
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {actionError && (
                <p role="alert" style={{ color: "#b3261e" }}>
                  {actionError}
                </p>
              )}

              <p style={{ marginTop: "1rem" }}>
                <button
                  type="button"
                  className="button"
                  onClick={onApprove}
                  disabled={busy !== null}
                >
                  {busy === "approve"
                    ? "Approving…"
                    : "Approve this lineup"}
                </button>{" "}
                <button
                  type="button"
                  className="button button-ghost"
                  onClick={onGenerate}
                  disabled={busy !== null}
                >
                  {busy === "generate"
                    ? "Regenerating…"
                    : "Regenerate proposals"}
                </button>
              </p>
              <p className="muted">
                <small>
                  Selected:{" "}
                  {editable
                    .map(
                      (l) =>
                        `L${l.line_number} ${nameOf(
                          roster,
                          l.player1_id,
                        )} / ${nameOf(roster, l.player2_id)}`,
                    )
                    .join(" · ")}
                </small>
              </p>
            </>
          )}
        </>
      )}
    </section>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────

function PlayerSelect({
  roster,
  value,
  disabled,
  onChange,
}: {
  roster: PlayerRef[];
  value: string;
  disabled: boolean;
  onChange: (id: string) => void;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        minWidth: 140,
        padding: "0.4rem",
        fontFamily: "inherit",
        fontSize: "0.95rem",
      }}
    >
      {roster.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name} ({p.ntrp.toFixed(1)})
        </option>
      ))}
    </select>
  );
}

// ─── Shared inline styles (match Season.tsx's spartan table) ───────────

const tableStyle: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: "0.5rem",
};
const theadRow: CSSProperties = {
  borderBottom: "1px solid #e5e5e5",
  textAlign: "left",
};
const tbodyRow: CSSProperties = {
  borderBottom: "1px solid #f0f0f0",
};
const th: CSSProperties = { padding: "0.5rem" };
const thLead: CSSProperties = {
  padding: "0.5rem 0.5rem 0.5rem 0",
};
const td: CSSProperties = { padding: "0.5rem" };
const tdLead: CSSProperties = {
  padding: "0.5rem 0.5rem 0.5rem 0",
};
